import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useColumnSettings } from "@eudonet/opal";
import { ListSettingsService } from "@/shared/infrastructure/ListSettingsService";
import { useAppToast } from "@/shared/composables/useAppToast";
import { buildColumnSettingsLabels } from "@/shared/composables/columnSettingsLabels";
import { logger } from "@/infrastructure/services/LoggerService";
export function useListSettings(options) {
    const { entity, labelOverrides, ...columnOptions } = options;
    const { t } = useI18n();
    const { toastError } = useAppToast();
    const service = new ListSettingsService();
    // Starts true so `dataReady` (filtersReady && !isLoading) stays closed at setup —
    // useSearch's `waitFor` watcher runs with `{ immediate: true }` during setup, before
    // onMounted's loadSettings() call flips this. On repeat SPA navigation filtersReady is
    // already module-cached true, so a false initial value here lets the first fetch fire
    // with the un-restored default sort before settings load (AC 7 default-then-resort).
    const isLoading = ref(true);
    const error = ref(null);
    // Per-column pixel widths the user has resized. Bound to the DataTable's
    // `column-sizing` prop and persisted alongside visibility/order/pinning.
    const widths = ref({});
    const activeSort = ref(null);
    const columnSettings = useColumnSettings({
        ...columnOptions,
        labels: computed(() => buildColumnSettingsLabels(t, labelOverrides?.(t))),
    });
    async function loadSettings() {
        isLoading.value = true;
        try {
            const saved = await service.getListSettings(entity);
            if (saved) {
                Object.entries(saved.visibility).forEach(([key, value]) => {
                    columnSettings.visibility[key] = value;
                });
                columnSettings.setPinning({ left: saved.pinning.left, right: saved.pinning.right });
                // A persisted `order` only lists the keys that existed when the user last
                // saved. Columns shipped since then are absent — assigning `saved.order`
                // verbatim would drop them from the rendered table. Keep the user's order
                // for keys that still exist, then append any default keys missing from the
                // persisted list so newly added columns remain reachable in the drawer.
                const defaultKeys = columnSettings.defaultSettingsItems.value.map((col) => col.key);
                const knownKeys = new Set(defaultKeys);
                const orderedKnown = saved.order.filter((key) => knownKeys.has(key));
                const appendedKeys = defaultKeys.filter((key) => !orderedKnown.includes(key));
                columnSettings.order.value = [...orderedKnown, ...appendedKeys];
                // Hydrate persisted widths, dropping any keys for columns that no longer
                // exist (mirrors the stale-key reconciliation applied to `order`).
                if (saved.widths) {
                    widths.value = Object.fromEntries(Object.entries(saved.widths).filter(([key]) => knownKeys.has(key)));
                }
                // Restore the active sort only when the sorted column still exists AND is
                // currently visible. A stale key (column removed from the code) fails the
                // knownKeys check; a hidden column fails the visibility check — both fall
                // back to null so the table renders in its natural unsorted state.
                const sortCol = saved.activeSort?.column;
                activeSort.value =
                    sortCol &&
                        knownKeys.has(sortCol) &&
                        columnSettings.visibility[sortCol] !== false
                        ? { column: sortCol, direction: saved.activeSort.direction }
                        : null;
                // OPAL only enforces locked-column invariants at construction, handleSave
                // and handleReset. Direct hydration bypasses those guards, so we re-assert:
                // disabled columns must stay visible and pinned-disabled columns must stay
                // in pinning.left, regardless of what the persisted state says.
                columnSettings.defaultSettingsItems.value.forEach((col) => {
                    if (col.disabled) {
                        columnSettings.visibility[col.key] = true;
                    }
                    if (!col.disabled || !col.pinned)
                        return;
                    // Read per iteration: the body below reassigns pinning.value.
                    const pinning = columnSettings.pinning.value;
                    const pinnedLeft = pinning.left ?? [];
                    if (!pinnedLeft.includes(col.key)) {
                        columnSettings.pinning.value = {
                            left: [col.key, ...pinnedLeft],
                            right: pinning.right,
                        };
                    }
                });
            }
        }
        catch (err) {
            logger.error("Failed to load list settings:", err);
            error.value = err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function persist() {
        try {
            await service.saveListSettings(entity, {
                version: 1,
                visibility: { ...columnSettings.visibility },
                order: columnSettings.order.value,
                pinning: {
                    left: columnSettings.pinning.value.left ?? [],
                    right: columnSettings.pinning.value.right ?? [],
                },
                widths: { ...widths.value },
                activeSort: activeSort.value ?? undefined,
            });
        }
        catch (err) {
            logger.error("Failed to save list settings:", err);
            toastError("columnSettings.toastSaveError");
        }
    }
    async function handleSave(cols) {
        columnSettings.handleSave(cols);
        // If the user just hid the sorted column, clear the sort so the table
        // doesn't remain sorted by an invisible column. The activeSort watcher
        // in the consuming view picks up the null change and calls clearSort().
        if (activeSort.value && columnSettings.visibility[activeSort.value.column] === false) {
            activeSort.value = null;
        }
        await persist();
    }
    // Fired once a DataTable column-resize gesture ends. Record the new widths and
    // persist them with the rest of the settings.
    function handleSizingChange(sizing) {
        widths.value = { ...sizing };
        void persist();
    }
    async function handleSortChange(sort) {
        activeSort.value = sort;
        await persist();
    }
    // Override OPAL's reset to also clear widths and activeSort. The drawer emits
    // `reset` then `save`, so the paired handleSave persists the cleared state —
    // handleReset itself must not persist (keeps the single-round-trip reset contract).
    const baseHandleReset = columnSettings.handleReset;
    function handleReset() {
        baseHandleReset();
        widths.value = {};
        activeSort.value = null;
    }
    onMounted(() => {
        loadSettings();
    });
    return {
        ...columnSettings,
        widths,
        activeSort,
        handleSave,
        handleSizingChange,
        handleSortChange,
        handleReset,
        isLoading,
        error,
    };
}
